import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "remodeling-web-design-colorado",
  metaTitle: "Remodeling Contractor Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom remodeling web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Colorado Springs digital marketing", to: "/colorado-springs" },
  ],
  currentLabel: "Remodeling Contractor Web Design Colorado",
  eyebrow: "Remodeling Contractor Web Design — Colorado",
  h1: (
    <>
      Remodeling Contractor <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded remodeling websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Remodeling websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first remodeling sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Remodeling Contractor web design in Colorado",
    body: "Remodeling Contractor web design is the practice of building conversion-focused, mobile-first websites for remodeling businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with portfolio galleries that don't tank PageSpeed." },
    { title: "Room-type service pages", body: "Kitchen, bathroom, basement, whole-home, addition, ADU — each ranks independently." },
    { title: "Portfolio galleries", body: "Lazy-loaded project galleries with image schema." },
    { title: "In-home consultation booking", body: "Real-time in-home consultation booking with your CRM." },
    { title: "Design-build process pages", body: "Process, financing, and timeline content captures research traffic." },
    { title: "Schema and SEO at launch", body: "LocalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Room-type pages outrank generic remodeling", body: "Kitchen, bathroom, and basement are different buyers, different budgets, different pages." },
    { heading: "Portfolio + process close the consult", body: "Galleries + clear design-build process above the fold close the consultation." },
    { heading: "Whole-home and ADU capture premium", body: "High-budget projects deserve dedicated pages with proper process and financing content." },
  ],
  relatedLinks: [
    { to: "/colorado-springs", label: "Colorado Springs digital marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a remodeling website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a remodeling website?", a: "Typical timeline is 4–6 weeks for a 12–20 page remodeling site with full service and service-area pages. Larger multi-location remodeling businesses can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs remodeling businesses?", a: "No — we build remodeling sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Remodeling Contractor Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for remodeling businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/remodeling-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
